import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { RestEndpoint } from '../../../constants/rest-endpoint.constants';

export interface AvailableDeal {
    id: string;
    dealNumber: string;
    originatorId: string;
    investorId?: string;
    fundingSourceId?: string;
    grossAmount: number;
    discountAmount: number;
    feesAmount: number;
    netDisbursement: number;
    expectedYield: number;
    receivableCount?: number;
    // Fractional investment fields
    targetAmount?: number;
    raisedAmount?: number;
    investorCount?: number;
    minInvestment?: number;
    maxInvestment?: number;
    // Tokenization fields
    tokenProjectId?: string;
    tokenProject?: {
        id: string;
        contractAddress?: string;
        standard: string;
        chainId: number;
        status: string;
        tokenName?: string;
        tokenSymbol?: string;
    };
    // Pricing template for transparency
    pricingTemplateId?: string;
    pricingTemplate?: {
        id: string;
        name: string;
        baseSpread: number;
        adminFee: number;
        reservePercentage?: number;
        minTermDays?: number;
        maxTermDays?: number;
    };
    // Investment details breakdown
    investmentDetails?: {
        grossAmount: number;
        discountAmount: number;
        feesAmount: number;
        netDisbursement: number;
        expectedYield: number;
        expectedYieldPercent: string;
        breakdown?: {
            baseSpreadPercent: string;
            adminFeePercent: string;
            reservePercent?: string;
            investorReturnFormula: string;
        };
    };
    // Receivables with documents
    receivables?: Array<{
        id: string;
        externalReference?: string;
        grossAmount: number;
        netAmount: number;
        dueDate: Date;
        status: string;
        documents: Array<{
            id: string;
            documentType: string;
            fileName: string;
            fileUrl?: string;
            createdAt: Date;
        }>;
    }>;
    // Contract document fields
    contractDocumentUrl?: string;
    contractDocumentName?: string;
    contractRequired?: boolean;
    status: 'OPEN_FOR_INVESTMENT' | 'FULLY_FUNDED' | 'APPROVED' | 'FUNDED' | 'DISBURSED' | 'PARTIALLY_SETTLED' | 'SETTLED' | 'PENDING_APPROVAL' | 'REJECTED';
    createdAt: Date;
    updatedAt: Date;
}

export interface Receivable {
    id: string;
    originatorId: string;
    receivableTypeId?: string;
    debtorId?: string;
    grossAmount: number;
    netAmount: number;
    currency: string;
    issueDate: Date;
    dueDate: Date;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DISCOUNTED' | 'SETTLED' | 'DEFAULT';
    externalReference?: string;
    invoiceNumber?: string;
    invoiceKey?: string;
    createdAt: Date;
    updatedAt: Date;
}

export interface Charge {
    id: string;
    installmentId?: string;
    originatorId: string;
    dealId?: string;
    referenceCode: string;
    chargeType: 'PIX' | 'BOLETO';
    amount: number;
    dueDate: Date;
    paidAmount?: number;
    paidAt?: Date;
    status: 'CREATED' | 'SENT' | 'PAID' | 'OVERDUE' | 'EXPIRED' | 'CANCELLED';
    pixEmv?: string;
    boletoLine?: string;
    boletoUrl?: string;
    createdAt: Date;
    updatedAt: Date;
}

export interface Settlement {
    id: string;
    dealId: string;
    settlementDate: Date;
    grossAmount: number;
    status: string;
    createdAt: Date;
}

export interface PortfolioPerformance {
    totalInvested: number;
    activeInvestments: number;
    expectedReturn: number;
    availableBalance: number;
    totalYield: number;
    defaultRate: number;
    recoveryRate: number;
    // Extended fields
    positions?: InvestorPosition[];
    totalProfit?: number;
}

export interface OriginatorDashboard {
    totalReceivables: number;
    activeDeals: number;
    pendingApproval: number;
    totalAmount: number;
    recentActivity: any[];
}

export interface DealSummary {
    totalDeals: number;
    totalGrossAmount: number;
    totalNetAmount: number;
    averageYield: number;
    byStatus: { status: string; count: number; amount: number }[];
}

export interface InvestorPosition {
    id: string;
    dealId: string;
    investorId: string;
    fundingSourceId?: string;
    investmentAmount: number; // Valor investido em BRL
    unitsAllocated: number;
    unitsMinted: number;
    unitsBurned: number;
    status: 'ALLOCATED' | 'PENDING' | 'MINTED' | 'PARTIALLY_BURNED' | 'BURNED';
    mintTxHash?: string;
    burnTxHash?: string;
    createdAt: Date;
    updatedAt?: Date;
    // Dados expandidos do deal
    deal?: {
        dealNumber: string;
        grossAmount: number;
        netDisbursement: number;
        expectedYield: number;
        status: string;
        pricingTemplate?: {
            id?: string;
            name?: string;
            baseSpread?: number;
            adminFee?: number;
            reservePercentage?: number;
        };
    };
}

export interface TokenizationSummary {
    totalPositions: number;
    totalAllocated?: number;
    totalMinted: number;
    totalBurned: number;
    totalValue: number;
    status?: string;
    positions: InvestorPosition[];
}

@Injectable({
    providedIn: 'root'
})
export class CreditService {
    constructor(
        private readonly http: HttpClient
    ) { }

    // ==================== DEALS ====================

    public getAvailableDeals(limit: number = 10, offset: number = 0): Observable<AvailableDeal[]> {
        const params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        return this.http.get<any>(RestEndpoint.credit.deals.available, { params }).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading available deals:', error);
                return of([]);
            })
        );
    }

    public getDealById(id: string): Observable<AvailableDeal | null> {
        return this.http.get<any>(RestEndpoint.credit.deals.getById(id)).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading deal:', error);
                return of(null);
            })
        );
    }

    public createDeal(deal: any): Observable<any> {
        return this.http.post<any>(RestEndpoint.credit.deals.create, deal).pipe(
            map((response: any) => response || response),
            catchError((error) => {
                console.error('Error creating deal:', error);
                throw error;
            })
        );
    }

    /**
     * Commit investment to a deal (investor assigns themselves and funding source)
     * This is called when an investor decides to invest in an OPEN_FOR_INVESTMENT deal
     * @param dealId - The deal to invest in
     * @param investorId - The investor's user ID
     * @param fundingSourceId - Optional funding source ID (for token payments)
     * @param amount - The investment amount in the tenant base currency
     * @param paymentMethod - The payment method type (PIX, TED, TOKEN)
     * @param contractAccepted - Whether the user accepted the contract (required if deal.contractRequired)
     */
    public commitInvestment(
        dealId: string, 
        investorId: string, 
        fundingSourceId: string | null, 
        amount: number,
        paymentMethod?: string,
        contractAccepted?: boolean
    ): Observable<AvailableDeal> {
        const body: any = {
            investorId,
            amount,
            paymentMethod,
            contractAccepted
        };
        
        // Only include fundingSourceId if it's a valid value
        if (fundingSourceId) {
            body.fundingSourceId = fundingSourceId;
        }
        
        return this.http.post<any>(RestEndpoint.credit.deals.invest(dealId), body).pipe(
            map((response: any) => response || response),
            catchError((error) => {
                console.error('Error committing investment:', error);
                throw error;
            })
        );
    }

    // ==================== RECEIVABLES ====================

    public getReceivablesByOriginator(originatorId: string, status?: string): Observable<Receivable[]> {
        let params = new HttpParams();
        if (status && status !== 'all') {
            params = params.set('status', status);
        }

        return this.http.get<any>(RestEndpoint.credit.receivables.getByOriginator(originatorId), { params }).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading receivables:', error);
                return of([]);
            })
        );
    }

    public getReceivableById(id: string): Observable<Receivable | null> {
        return this.http.get<any>(RestEndpoint.credit.receivables.getById(id)).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading receivable:', error);
                return of(null);
            })
        );
    }

    public createReceivable(receivable: any): Observable<Receivable> {
        return this.http.post<any>(RestEndpoint.credit.receivables.create, receivable).pipe(
            map((response: any) => response || response),
            catchError((error) => {
                console.error('Error creating receivable:', error);
                throw error;
            })
        );
    }

    // ==================== CHARGES ====================

    public getChargesByOriginator(originatorId: string, status?: string): Observable<Charge[]> {
        let params = new HttpParams();
        if (status && status !== 'all') {
            params = params.set('status', status);
        }

        return this.http.get<any>(RestEndpoint.credit.charges.getByOriginator(originatorId), { params }).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading charges:', error);
                return of([]);
            })
        );
    }

    public getChargeById(id: string): Observable<Charge | null> {
        return this.http.get<any>(RestEndpoint.credit.charges.getById(id)).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading charge:', error);
                return of(null);
            })
        );
    }

    // ==================== REPORTS ====================

    public getPortfolioPerformance(investorId?: string, startDate?: string, endDate?: string): Observable<PortfolioPerformance> {
        let params = new HttpParams();
        if (investorId) params = params.set('investorId', investorId);
        if (startDate) params = params.set('startDate', startDate);
        if (endDate) params = params.set('endDate', endDate);
        
        return this.http.get<any>(RestEndpoint.credit.reports.portfolioPerformance, { params }).pipe(
            map((response: any) => response || this.getDefaultPortfolioPerformance()),
            catchError((error) => {
                console.error('Error loading portfolio performance:', error);
                return of(this.getDefaultPortfolioPerformance());
            })
        );
    }

    public getDealSummary(dealId?: string, originatorId?: string, investorId?: string, startDate?: string, endDate?: string): Observable<DealSummary | null> {
        let params = new HttpParams();
        if (dealId) params = params.set('dealId', dealId);
        if (originatorId) params = params.set('originatorId', originatorId);
        if (investorId) params = params.set('investorId', investorId);
        if (startDate) params = params.set('startDate', startDate);
        if (endDate) params = params.set('endDate', endDate);
        
        return this.http.get<any>(RestEndpoint.credit.reports.dealSummary, { params }).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading deal summary:', error);
                return of(null);
            })
        );
    }

    public getChargeStatusReport(dealId?: string, originatorId?: string, startDate?: string, endDate?: string): Observable<any> {
        let params = new HttpParams();
        if (dealId) params = params.set('dealId', dealId);
        if (originatorId) params = params.set('originatorId', originatorId);
        if (startDate) params = params.set('startDate', startDate);
        if (endDate) params = params.set('endDate', endDate);
        
        return this.http.get<any>(RestEndpoint.credit.reports.chargeStatus, { params }).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading charge status report:', error);
                return of(null);
            })
        );
    }

    // ==================== TOKENIZATION ====================

    public getInvestorPositions(investorId: string): Observable<InvestorPosition[]> {
        return this.http.get<any>(RestEndpoint.credit.tokenization.positionsByInvestor(investorId)).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading investor positions:', error);
                return of([]);
            })
        );
    }

    public getDealTokenization(dealId: string): Observable<TokenizationSummary | null> {
        return this.http.get<any>(RestEndpoint.credit.tokenization.getDealTokenization(dealId)).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading deal tokenization:', error);
                return of(null);
            })
        );
    }

    public getDealTokenizationSummary(dealId: string): Observable<TokenizationSummary | null> {
        return this.http.get<any>(RestEndpoint.credit.tokenization.getDealSummary(dealId)).pipe(
            map((response: any) => response || null),
            catchError((error) => {
                console.error('Error loading deal tokenization summary:', error);
                return of(null);
            })
        );
    }

    public getTokenizationSummary(investorId: string): Observable<TokenizationSummary> {
        return this.getInvestorPositions(investorId).pipe(
            map((positions: InvestorPosition[]) => {
                const summary: TokenizationSummary = {
                    totalPositions: positions.length,
                    totalAllocated: positions.reduce((sum, p) => sum + this.toNumber(p.unitsAllocated ?? p.investmentAmount ?? 0), 0),
                    totalMinted: positions.reduce((sum, p) => sum + this.toNumber(p.unitsMinted ?? 0), 0),
                    totalBurned: positions.reduce((sum, p) => sum + this.toNumber(p.unitsBurned ?? 0), 0),
                    // totalValue: investmentAmount é mais relevante que tokens ativos
                    totalValue: positions.reduce((sum, p) => sum + this.toNumber(p.investmentAmount ?? p.unitsAllocated ?? 0), 0),
                    positions: positions
                };
                return summary;
            }),
            catchError((error) => {
                console.error('Error calculating tokenization summary:', error);
                return of({
                    totalPositions: 0,
                    totalMinted: 0,
                    totalBurned: 0,
                    totalValue: 0,
                    positions: []
                });
            })
        );
    }

    // ==================== DEAL DETAILS ====================

    public getDealReceivables(dealId: string): Observable<Receivable[]> {
        return this.http.get<any>(RestEndpoint.credit.deals.getReceivables(dealId)).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading deal receivables:', error);
                return of([]);
            })
        );
    }

    public getDealCharges(dealId: string): Observable<Charge[]> {
        return this.http.get<any>(RestEndpoint.credit.deals.getCharges(dealId)).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading deal charges:', error);
                return of([]);
            })
        );
    }

    public getDealSettlements(dealId: string): Observable<Settlement[]> {
        return this.http.get<any>(RestEndpoint.credit.deals.getSettlements(dealId)).pipe(
            map((response: any) => response || []),
            catchError((error) => {
                console.error('Error loading deal settlements:', error);
                return of([]);
            })
        );
    }

    // ==================== HELPERS ====================

    private getDefaultPortfolioPerformance(): PortfolioPerformance {
        return {
            totalInvested: 0,
            activeInvestments: 0,
            expectedReturn: 0,
            availableBalance: 0,
            totalYield: 0,
            defaultRate: 0,
            recoveryRate: 0
        };
    }

    /**
     * Calculate portfolio performance from investor positions
     * This is more accurate than the backend endpoint
     */
    public calculatePortfolioFromPositions(investorId: string): Observable<PortfolioPerformance> {
        return this.getInvestorPositions(investorId).pipe(
            map((positions: InvestorPosition[]) => {
                const totalInvested = positions.reduce(
                    (sum, p) => sum + this.toNumber(p.investmentAmount ?? p.unitsAllocated ?? 0),
                    0
                );
                const activeInvestments = positions.filter(p => 
                    p.status === 'ALLOCATED' || p.status === 'MINTED' || p.status === 'PENDING'
                ).length;
                
                // Calculate expected return based on deal yields
                let expectedReturn = 0;
                positions.forEach(p => {
                    const investment = this.toNumber(p.investmentAmount ?? p.unitsAllocated ?? 0);
                    const yieldRate = this.normalizeRateToDecimal(p.deal?.expectedYield || 0);
                    const adminFee = this.normalizeRateToDecimal(p.deal?.["pricingTemplate"]?.adminFee || 0);
                    const netYieldRate = Math.max(0, yieldRate - adminFee);
                    expectedReturn += investment * (1 + netYieldRate);
                });

                // Calculate average yield
                const totalYield = totalInvested > 0 
                    ? ((expectedReturn - totalInvested) / totalInvested) * 100 
                    : 0;

                return {
                    totalInvested,
                    activeInvestments,
                    expectedReturn,
                    availableBalance: 0, // Would need to come from another source
                    totalYield,
                    defaultRate: 0,
                    recoveryRate: 0,
                    positions // Include positions for detail view
                } as PortfolioPerformance;
            }),
            catchError((error) => {
                console.error('Error calculating portfolio:', error);
                return of(this.getDefaultPortfolioPerformance());
            })
        );
    }

    private normalizeRateToDecimal(rate: number): number {
        if (!rate) return 0;
        return rate >= 1 ? rate / 100 : rate;
    }

    private toNumber(value: number | string): number {
        if (typeof value === 'number') return value;
        const trimmed = (value || '').toString().replace(/\s/g, '');
        let normalized = trimmed;
        const dotMatches = trimmed.match(/\./g);
        const dotCount = dotMatches ? dotMatches.length : 0;
        const hasComma = trimmed.includes(',');
        if (hasComma && dotCount > 0) {
            normalized = trimmed.replace(/\./g, '').replace(',', '.');
        } else if (hasComma) {
            normalized = trimmed.replace(',', '.');
        } else if (dotCount > 1) {
            normalized = trimmed.replace(/\./g, '');
        }
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    // ==================== ACCESS VALIDATION ====================

    /**
     * Check if current logged user has originator status (registered as originator party)
     */
    public checkOriginatorStatus(): Observable<{ isOriginator: boolean; partyId?: string }> {
        return this.http.get<any>(RestEndpoint.credit.parties.checkOriginator()).pipe(
            map((response: any) => {
                const data = response?.data || response;
                return {
                    isOriginator: data?.isOriginator || data?.type === 'ORIGINATOR' || false,
                    partyId: data?.id || data?.partyId
                };
            }),
            catchError((error) => {
                console.error('Error checking originator status:', error);
                // Return false on error, denying access by default
                return of({ isOriginator: false });
            })
        );
    }

    // ==================== STATUS HELPERS ====================

    public getStatusLabel(status: string): string {
        const statusMap: { [key: string]: string } = {
            'PENDING': 'credit.status.pending',
            'PENDING_APPROVAL': 'credit.status.pending_approval',
            'OPEN_FOR_INVESTMENT': 'credit.status.open_for_investment',
            'FULLY_FUNDED': 'credit.status.fully_funded',
            'APPROVED': 'credit.status.approved',
            'REJECTED': 'credit.status.rejected',
            'FUNDED': 'credit.status.funded',
            'DISBURSED': 'credit.status.disbursed',
            'PARTIALLY_SETTLED': 'credit.status.partially_settled',
            'SETTLED': 'credit.status.settled',
            'CANCELLED': 'credit.status.cancelled',
            'DEFAULT': 'credit.status.default',
            'CREATED': 'credit.status.created',
            'SENT': 'credit.status.sent',
            'PAID': 'credit.status.paid',
            'OVERDUE': 'credit.status.overdue',
            'EXPIRED': 'credit.status.expired',
        };
        return statusMap[status] || 'credit.status.unknown';
    }

    public getStatusColor(status: string): string {
        const colorMap: { [key: string]: string } = {
            'PENDING': 'warn',
            'PENDING_APPROVAL': 'warn',
            'OPEN_FOR_INVESTMENT': 'accent',
            'APPROVED': 'primary',
            'REJECTED': 'accent',
            'FUNDED': 'primary',
            'DISBURSED': 'primary',
            'PARTIALLY_SETTLED': 'warn',
            'SETTLED': 'primary',
            'CANCELLED': 'accent',
            'DEFAULT': 'accent',
            'CREATED': 'warn',
            'SENT': 'primary',
            'PAID': 'primary',
            'OVERDUE': 'accent',
            'EXPIRED': 'accent',
        };
        return colorMap[status] || 'basic';
    }
}
